import React, { useMemo, useState } from 'react'
import { Button, DataTable, DatePicker, Input, PageHeader } from '@/shared/ui'
import type { Column, LedgerItemDTO, ProblemOrderDTO } from '@/shared/api'
import {
  useCourierLedger,
  useCourierLedgerDetails,
  useRecalculateLedger,
  useProblemOrders,
  useExportLedger,
} from '@/shared/hooks'

// Типы записей журнала начислений (синхронизированы с backend)
const TYPE_LABELS: Record<number, string> = {
  1: 'Начисление',
  2: 'Сторно',
  3: 'Перерасчёт',
}

const ENTRY_TYPE_OPTIONS = [
  { value: 0, label: 'Все' },
  { value: 1, label: 'Начисление' },
  { value: 2, label: 'Сторно' },
  { value: 3, label: 'Перерасчёт' },
]

// Быстрые представления (вкладки)
const TABS = [
  { key: 'active', label: 'Действующие', value: 1 },
  { key: 'reverted', label: 'Сторнированные', value: 2 },
  { key: 'recalc', label: 'Перерасчёты', value: 3 },
] as const

// Границы суток считаем в московском времени (UTC+3), как в разделе модерации.
// date_from — начало мсковских суток, date_to — начало следующих суток
// (исключительно), чтобы диапазон включал весь выбранный день.
const dayStartMsk = (d: Date): number =>
  Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 1000) - 3 * 3600
const dayEndMskExclusive = (d: Date): number => dayStartMsk(d) + 86400

export const CourierLedgerPage: React.FC = () => {
  const [courierId, setCourierId] = useState('')
  const [entryType, setEntryType] = useState<number>(1)
  const [shopOrderId, setShopOrderId] = useState('')
  const [dateFrom, setDateFrom] = useState<Date | null>(null)
  const [dateTo, setDateTo] = useState<Date | null>(null)

  const [selected, setSelected] = useState<number | null>(null)
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')

  // Параметры фильтрации, отправляемые в список и в экспорт
  const queryParams = useMemo(() => {
    const params: Record<string, unknown> = {}
    if (courierId) params.courier_id = Number(courierId)
    if (entryType) params.entry_type = entryType
    if (shopOrderId) params.shop_order_id = shopOrderId
    if (dateFrom) params.date_from = dayStartMsk(dateFrom)
    if (dateTo) params.date_to = dayEndMskExclusive(dateTo)
    return params
  }, [courierId, entryType, shopOrderId, dateFrom, dateTo])

  const { data, isLoading } = useCourierLedger(queryParams)
  const { data: problems, isLoading: problemsLoading } = useProblemOrders()
  const recalc = useRecalculateLedger()
  const exportLedger = useExportLedger()

  const { data: details, isLoading: detailsLoading } = useCourierLedgerDetails(selected)

  const total = useMemo(
    () => (data?.items ?? []).reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
    [data]
  )

  const columns: Column<LedgerItemDTO>[] = [
    {
      key: 'created_at_formatted',
      title: 'Дата',
      render: (_, row) => row.created_at_formatted || '—',
    },
    { key: 'courier_id', title: 'Курьер' },
    { key: 'shop_order_id', title: 'Заказ', render: (_, row) => row.shop_order_id ?? '—' },
    {
      key: 'entry_type',
      title: 'Тип',
      render: (_, row) => TYPE_LABELS[row.entry_type] || row.entry_type,
    },
    { key: 'amount', title: 'Сумма' },
  ]

  const problemColumns: Column<ProblemOrderDTO>[] = [
    { key: 'number', title: 'Заказ', render: (_, row) => row.number ?? row.shop_order_id },
    {
      key: 'courier_name',
      title: 'Курьер',
      render: (_, row) => row.courier_name ?? row.courier_id ?? '—',
    },
    { key: 'status', title: 'Статус' },
    { key: 'reason', title: 'Причина' },
  ]

  const handleRecalculate = () => {
    if (selected === null || !amount || !reason) return
    recalc.mutate(
      { id: selected, newAmount: Number(amount), reason },
      {
        onSuccess: () => {
          setAmount('')
          setReason('')
        },
      }
    )
  }

  const handleExport = async () => {
    const blob = await exportLedger.mutateAsync(queryParams)
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'courier-ledger.csv')
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <PageHeader
        title="Начисления курьерам"
        description="Журнал начислений и ручные перерасчёты"
      />

      <div className="p-6 space-y-4">
        {/* Вкладки быстрых представлений */}
        <div className="flex flex-wrap gap-2">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={
                entryType === tab.value
                  ? 'admin-button admin-button-primary'
                  : 'admin-button admin-button-secondary'
              }
              onClick={() => setEntryType(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Фильтры */}
        <div className="admin-card p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-32">
              <Input
                label="ID курьера"
                type="number"
                value={courierId}
                onChange={(e) => setCourierId(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Тип записи</label>
              <select
                className="admin-input"
                value={entryType}
                onChange={(e) => setEntryType(Number(e.target.value))}
              >
                {ENTRY_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-40">
              <Input
                label="Номер заказа"
                value={shopOrderId}
                onChange={(e) => setShopOrderId(e.target.value)}
              />
            </div>
            <div className="w-40 space-y-1">
              <label className="block text-sm font-medium text-gray-700">Дата с</label>
              <DatePicker value={dateFrom} onChange={setDateFrom} />
            </div>
            <div className="w-40 space-y-1">
              <label className="block text-sm font-medium text-gray-700">Дата по</label>
              <DatePicker value={dateTo} onChange={setDateTo} />
            </div>
            <Button
              variant="secondary"
              onClick={handleExport}
              isLoading={exportLedger.isPending}
            >
              📄 Сформировать отчёт по начислениям
            </Button>
          </div>
        </div>

        {/* Итого к выплате по отображаемым записям */}
        <div className="admin-card flex items-center justify-between p-4">
          <span className="font-semibold text-gray-900">Итого к выплате</span>
          <span className="text-lg font-semibold text-gray-900">
            {total.toLocaleString('ru-RU')} ₽
          </span>
        </div>

        <DataTable
          data={data?.items ?? []}
          columns={columns}
          loading={isLoading}
          emptyMessage="Начислений нет"
          onRowClick={(row) => setSelected(row.courier_ledger_entry_id)}
        />

        {/* Заказы без настроенного тарифа */}
        <div className="space-y-2">
          <h3 className="font-semibold text-gray-900">
            ⚠ Заказы без настроенного тарифа
          </h3>
          <p className="text-sm text-gray-500">
            По этим заказам начисление не сформировано. Настройте тариф курьеру в разделе
            «Тарифы курьеров» или назначьте индивидуальный тариф в разделе «Курьеры».
          </p>
          <DataTable
            data={problems?.items ?? []}
            columns={problemColumns}
            loading={problemsLoading}
            emptyMessage="Проблемных заказов нет"
          />
        </div>

        {selected !== null && (
          <div className="admin-card p-4">
            <h3 className="mb-2 font-semibold text-gray-900">
              Детализация записи #{selected}
            </h3>
            <pre className="mb-4 whitespace-pre-wrap text-xs text-gray-700">
              {detailsLoading ? 'Загрузка...' : details?.breakdown || '—'}
            </pre>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <input
                className="admin-input"
                type="number"
                placeholder="Новая сумма"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <input
                className="admin-input"
                placeholder="Причина"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <Button
                variant="primary"
                disabled={!amount || !reason || recalc.isPending}
                isLoading={recalc.isPending}
                onClick={handleRecalculate}
              >
                Перерасчитать
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
